import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { meApi } from '@/api/me'

/**
 * 当前账号的权限码快照（权限模型 v2）：按钮级鉴权与菜单同源于服务端的判定链
 * （公司可用权限 ∩ 角色权限码 ∩ 系统已挂载服务）。
 *
 * 权限码按系统下发，因此和菜单一样挂在**控制台自身所属系统**上（appStore.consoleSystemUuid）。
 */
export const usePermStore = defineStore('perm', () => {
  /** 权威权限码：只由服务端快照填充，前端不推断、不补默认值。 */
  const codes = ref<string[]>([])
  /** 服务端权限版本号：与 access_token 中的 permVersion 一致，用于判断是否需要重拉。 */
  const permVersion = ref<string | null>(null)
  /** 当前快照所属系统：换系统必须重拉取，否则拿 A 的权限码去判定 B 的按钮。 */
  const loadedSystemUuid = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function load(systemUuid: string, force = false): Promise<void> {
    if (!force && loadedSystemUuid.value === systemUuid) {
      return
    }
    loading.value = true
    error.value = null
    try {
      const snapshot = await meApi.permissions({ systemUuid, scope: 'management' })
      codes.value = snapshot.codes ?? []
      permVersion.value = snapshot.permVersion ?? null
      loadedSystemUuid.value = systemUuid
    } catch (e) {
      // 拉取失败必须清空：留着上一个系统的权限码，等于把它的按钮继续显示给当前系统
      codes.value = []
      loadedSystemUuid.value = null
      error.value = e instanceof Error ? e.message : '权限加载失败'
    } finally {
      loading.value = false
    }
  }

  function has(code: string): boolean {
    return codes.value.includes(code)
  }

  function hasAny(list: string[]): boolean {
    return list.some((code) => codes.value.includes(code))
  }

  /** 登出清理：与菜单同批重置，避免下一次登录先闪出上一个账号的按钮。 */
  function reset(): void {
    codes.value = []
    permVersion.value = null
    loadedSystemUuid.value = null
    error.value = null
  }

  return {
    codes,
    permVersion,
    loadedSystemUuid,
    loading,
    error,
    load,
    has,
    hasAny,
    reset,
    total: computed(() => codes.value.length),
  }
})
